/**
 * Resuelve, por id, los operadores que aparecen como remitentes en un chat.
 *
 * Devuelve para cada `senderId` su nombre completo y su ficha de contacto en el store de la
 * conversación, para poder mostrar `[Ventas Puntoseco]` como link en vez del `senderName` guardado.
 *
 * **Por qué no alcanza con `senderName`:** ese nombre se copia del token al momento de enviar y queda
 * congelado. En la base el mismo operador figura como `Ventas` y como `user:5`, según qué traía el
 * token ese día. Resolviendo por id, los mensajes viejos también se muestran bien.
 *
 * Se pide **una vez por operador** y queda cacheado mientras dure el componente: un chat tiene un
 * puñado de remitentes distintos, no uno por mensaje.
 */
import { useEffect, useRef, useState } from "react";
import { findOperatorContact, OperatorRef } from "@/services/contacts.api";

export function useOperatorRefs(senderIds: number[], storeCode?: string) {
  const [refs, setRefs] = useState<Record<number, OperatorRef>>({});
  /** Ids ya pedidos —resueltos o no— para no reintentar en loop los que no tienen contacto. */
  const pedidos = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!storeCode) return;
    // `senderId` 0 es un mensaje enviado desde el teléfono ("Celular"): no hay operador que resolver.
    const faltan = [...new Set(senderIds)].filter(
      (id) => id > 0 && !pedidos.current.has(`${storeCode}:${id}`),
    );
    if (!faltan.length) return;

    let vigente = true;
    void (async () => {
      for (const id of faltan) {
        pedidos.current.add(`${storeCode}:${id}`);
        const ref = await findOperatorContact(id, storeCode);
        if (!vigente) return;
        if (ref) setRefs((prev) => ({ ...prev, [id]: ref }));
      }
    })();
    return () => {
      vigente = false;
    };
  }, [senderIds.join(","), storeCode]);

  // Al cambiar de store el caché deja de valer: el mismo usuario tiene otra ficha en cada store.
  useEffect(() => {
    pedidos.current.clear();
    setRefs({});
  }, [storeCode]);

  return refs;
}
